import React, { useEffect, useState } from 'react'
import { getAssetUrl } from '../../../helpers/backend_helper'
import {
  Button,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Form,
  FormGroup,
  FormFeedback,
} from 'reactstrap'
import { IAccessory, ICategory, IPhotoBooth } from './index'
import { toast } from 'react-toastify'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import Select from 'react-select'
import { customStylesLabel } from '../../../lib/util'

interface IProps {
  accessoryRecord: IAccessory | undefined
  isOpen: boolean
  editMode: boolean
  setOpenPopup: (arg: boolean) => void
  handleCreateOrUpdateAccessory: (arg: any) => Promise<string>
  categories: ICategory[]
  photoBooths: IPhotoBooth[]
}

const initAccessory: IAccessory = {
  name: '',
  imageLink: '',
  category: '',
  translations: {},
  parts: [],
  isActive: true,
  isActiveFor: []
}

const AccessoryModal: React.FC<IProps> = ({
  accessoryRecord,
  isOpen,
  editMode,
  handleCreateOrUpdateAccessory,
  setOpenPopup,
  categories,
  photoBooths
}) => {
  const [accessoryState, setAccessoryState] = useState<IAccessory>(initAccessory)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const hasEdit = (editMode && accessoryRecord) || false

  const boothOptions = photoBooths.map((booth) => ({
    value: booth._id,
    label: booth.name
  }))

  const formik: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: hasEdit ? accessoryRecord?.name : '',
      imageLink: hasEdit ? accessoryRecord?.imageLink : '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Isim zorunludur'),
      imageLink: Yup.string(),
    }),
    onSubmit: () => {
      handleSubmit()
    }
  })

  useEffect(() => {
    if (editMode && accessoryRecord) {
      setAccessoryState(accessoryRecord)
    } else {
      setAccessoryState(initAccessory)
    }

    if (isOpen) {
      formik.resetForm()
      setSelectedFile(null)
    }
  }, [isOpen, editMode, accessoryRecord])

  const togglePopup = () => {
    setOpenPopup(!isOpen)
  }

  const handleOnChange = (e: { target: { name: string; value: string } }) => {
    const { name, value } = e.target
    setAccessoryState({
      ...accessoryState,
      [name]: value
    })
    formik.setFieldValue(name, value)
  }

  const handleCategoryChange = (e: { target: { value: string } }) => {
    const value = e.target.value
    setAccessoryState({
      ...accessoryState,
      category: value
    })
    formik.setFieldValue('category', value)
  }

  const handleBoothsChange = (selectedOptions: any) => {
    const selectedIds = selectedOptions ? selectedOptions.map((opt: any) => opt.value) : []
    setAccessoryState({
      ...accessoryState,
      isActiveFor: selectedIds
    })
  }

  const handleSubmit = async () => {
    if (!editMode && !selectedFile) {
      toast.error('Lütfen bir görsel seçin')
      return
    }

    const formData = new FormData()
    if (selectedFile) {
      formData.append('image', selectedFile)
    } else if (editMode && accessoryState.imageLink) {
      formData.append('imageLink', accessoryState.imageLink)
    }
    formData.append('name', accessoryState.name || '')
    formData.append('isActiveFor', JSON.stringify(accessoryState.isActiveFor || []))
    formData.append('isActive', String(accessoryState.isActive))
    formData.append('translations', JSON.stringify(accessoryState.translations || {}))
    if (editMode && accessoryRecord?._id) {
      formData.append('_id', accessoryRecord._id)
    }

    const message = await handleCreateOrUpdateAccessory(formData)
    if (message === 'SUCCESSFULL') {
      toast.success(
        editMode ? 'Aksesuar basariyla guncellendi' : 'Yeni aksesuar basariyla olusturuldu'
      )
      setOpenPopup(false)
    } else {
      toast.error(message)
    }
  }

  const selectedBooths = boothOptions.filter((opt) =>
    accessoryState.isActiveFor?.includes(opt.value)
  )

  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen}
        toggle={togglePopup}
        scrollable={true}
        id='accessoryModal'
        size='lg'
      >
        <ModalHeader className='modal-title' id='accessoryModalTitle' toggle={togglePopup}>
          {editMode ? 'Aksesuar Duzenle' : 'Yeni Aksesuar Olustur'}
        </ModalHeader>
        <ModalBody>
          <Form
            className='needs-validation'
            onSubmit={(e) => {
              e.preventDefault()
              formik.handleSubmit()
              return false
            }}
          >
            <Row>
              <FormGroup>
                <Col md={12} className='mb-3'>
                  <Label htmlFor='name' className='form-label mb-1 fw-semibold'>
                    Isim
                  </Label>
                  <Input
                    type='text'
                    className='form-control'
                    id='name'
                    name='name'
                    placeholder='Aksesuar ismi'
                    value={accessoryState?.name}
                    onChange={handleOnChange}
                    invalid={formik.touched.name && formik.errors.name ? true : false}
                  />
                  {formik.touched.name && formik.errors.name ? (
                    <FormFeedback type='invalid'>{formik.errors.name}</FormFeedback>
                  ) : null}
                </Col>
              </FormGroup>

              <FormGroup>
                <Col md={12} className='mb-3'>
                  <Label htmlFor='imageFile' className='form-label mb-1 fw-semibold'>
                    Gorsel Yukle
                  </Label>
                  <Input
                    type='file'
                    className='form-control'
                    id='imageFile'
                    name='imageFile'
                    accept='image/*'
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null
                      setSelectedFile(file)
                      if (file) {
                        formik.setFieldValue('imageLink', file.name)
                      }
                    }}
                  />
                  {selectedFile && (
                    <div className='mt-2'>
                      <img
                        src={URL.createObjectURL(selectedFile)}
                        alt='Preview'
                        style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover', borderRadius: '8px' }}
                      />
                    </div>
                  )}
                  {!selectedFile && editMode && accessoryState.imageLink && (
                    <div className='mt-2'>
                      <img
                        src={getAssetUrl(accessoryState.imageLink)}
                        alt='Current'
                        style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover', borderRadius: '8px' }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none'
                        }}
                      />
                      <small className='d-block text-muted mt-1'>Mevcut gorsel</small>
                    </div>
                  )}
                </Col>
              </FormGroup>

              <FormGroup>
                <Col md={12} className='mb-3'>
                  <Label className='form-label mb-1 fw-semibold'>
                    Aktif Oldugu Booth'lar
                  </Label>
                  <div className='d-flex align-items-center gap-2 mb-2'>
                    <Button
                      color='soft-primary'
                      size='sm'
                      type='button'
                      onClick={() => {
                        setAccessoryState({ ...accessoryState, isActiveFor: boothOptions.map(b => b.value) })
                      }}
                    >
                      Select All
                    </Button>
                    <Button
                      color='soft-secondary'
                      size='sm'
                      type='button'
                      onClick={() => {
                        setAccessoryState({ ...accessoryState, isActiveFor: [] })
                      }}
                    >
                      Clear All
                    </Button>
                    <span className='text-muted fs-12'>
                      {selectedBooths.length} / {boothOptions.length} selected
                    </span>
                  </div>
                  <Select
                    isMulti
                    options={boothOptions}
                    value={selectedBooths}
                    onChange={handleBoothsChange}
                    placeholder='Booth seciniz...'
                    className='basic-multi-select'
                    classNamePrefix='select'
                    menuPortalTarget={document.body}
                    menuPosition='fixed'
                    styles={customStylesLabel}
                  />
                  <small className='text-muted'>
                    Bu aksesuarin hangi booth'larda gorunecegini secin
                  </small>
                </Col>
              </FormGroup>
            </Row>

            <div className='modal-footer'>
              <Button color='light' onClick={() => setOpenPopup(false)}>
                Kapat
              </Button>
              <Button color='primary' type='submit'>
                Kaydet
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  )
}

export default AccessoryModal
